import React, { useState, useEffect, useCallback } from "react";
import {
  Pagination,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BankerDocumentsView from "../deals/BankerDocumentsView";
import TermSheetView from "../deals/TermSheetView";
import { Deal } from "../../../validation/customer.validation";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArticleIcon from "@mui/icons-material/Article";
import DSCDetails from "./DSCDetails";
import NewLoanDialog from "./NewLoanDialog";
import ProceedsDialog from "./ProceedsDialog";
import { getAllBusinessCustomerLoan, updateLoanProceeds } from "../../../services/customer.service";
import {formatCurrency} from "../../../helper/currencyFormatter.helper";

interface LoanDeal {
  id: string;
  loanName: string;
  dealType: string;
  loanType: string;
  loanSize: number;
  payoffValue?: number;
  guaranteedName?: string | null;
  proceeds?: { purpose: string; amount: number }[];
  status: string;
  dscRatio?: string | number;
  customerBusinessId?: string;
  assignedBanks?: Array< {
    id: string;
    name: string;
    bankId: string;
    bank: { firstName: string; lastName: string; middleName: string };
  }>;
}

interface LoansTableProps {
  customerId: string;
  businessId: string;
  paginationData: {
    page: number;
    limit: number;
    total: number;
  };
  handlePageChange: (e: React.ChangeEvent<unknown>, page: number) => void;
  refreshKey?: number;
  onLoanCreated?: (loan?: any) => void;
  onOpenQuestionnaire?: (loan: LoanDeal) => void;
}

const LoansTable: React.FC<LoansTableProps> = ({
  customerId,
  businessId,
  paginationData,
  handlePageChange,
  refreshKey,
  onLoanCreated,
  onOpenQuestionnaire,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set<string>());
  const [loans, setLoans] = useState<LoanDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [newLoanDialogOpen, setNewLoanDialogOpen] = useState(false);
  const [dscModalOpen, setDscModalOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState(""); 
  const [viewingLoan, setViewingLoan] = useState<LoanDeal | null>(null);
  const [viewingBankerId, setViewingBankerId] = useState<string | null>(null);
  const [selectedLoan, setSelectedLoan] = useState({});
  const [proceedsDialogOpen, setProceedsDialogOpen] = useState(false);
  const [viewingProceedsLoan, setViewingProceedsLoan] = useState<LoanDeal | null>(null);
  const [savingProceeds, setSavingProceeds] = useState(false);


  const handleDscOpen = (busId: string, loan: object) => {
    setSelectedLoan(loan);
    setSelectedBusinessId(busId);
    setDscModalOpen(true);
  };

  const handleDscClose = () => {
    setDscModalOpen(false);
    setSelectedBusinessId("");
  };

  const toggleRow = useCallback(
    (loanId: string) => {
      const newExpanded = new Set(expandedRows);
      if (newExpanded.has(loanId)) {
        newExpanded.delete(loanId);
      } else {
        newExpanded.add(loanId);
      }
      setExpandedRows(newExpanded);
    },
    [expandedRows],
  );

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await getAllBusinessCustomerLoan(businessId);
      setLoans(response || []);
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchLoans();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleNewLoan = () => {
    setNewLoanDialogOpen(true);
  };

  const handleCloseNewLoan = () => {
    setNewLoanDialogOpen(false);
  };

  const handleProceedsClick = (loan: LoanDeal) => {
    setViewingProceedsLoan(loan);
    setProceedsDialogOpen(true);
  };

  const handleCloseProceedsDialog = () => {
    setProceedsDialogOpen(false);
    setViewingProceedsLoan(null);
  };

  const handleSaveProceeds = async (proceeds: { purpose: string; amount: number }[]) => {
    if (!viewingProceedsLoan) return;
    setSavingProceeds(true);
    try {
      const response = await updateLoanProceeds(viewingProceedsLoan.id, proceeds);
      setLoans((prev) =>
        prev.map((loan) =>
          loan.id === viewingProceedsLoan.id ? { ...loan, ...response } : loan
        )
      );
    } finally {
      setSavingProceeds(false);
    }
  };

  const totalPages = businessId
    ? Math.ceil(loans.length / 10)
    : Math.ceil(paginationData.total / paginationData.limit);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      {loans.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl shadow-xl border border-gray-200">
          <AttachMoneyIcon className="mx-auto h-16 w-16 text-gray-400 mb-6" />
          <Typography variant="h5" className="font-bold mb-2">
            No loans yet
          </Typography>
          <Typography className="text-gray-500 !mb-4">
            Create your first loan request from a business profile
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            className="!bg-green-600 hover:!bg-green-700 !text-white !rounded-xl !font-semibold"
            onClick={handleNewLoan}
          >
            Create New Loan
          </Button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-2xl border border-gray-200 shadow-lg">
              <thead>
                <tr className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <th></th>    
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Loan Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Deal Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Loan Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Loan Size
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Guaranteed Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-yellow-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loans.map((loan) => (
                  <React.Fragment key={loan.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-center w-12">
                      <IconButton
                        onClick={() => toggleRow(loan.id)}
                        size="small"
                        sx={{
                          '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.04)' },
                        }}
                      >
                        {expandedRows.has(loan.id) ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </td>
                    <td className="text-left px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {loan.loanName}
                    </td>

                      <td className="text-left px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                          {loan.dealType}
                        </span>
                      </td>

                      <td className="text-left px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                          {loan.loanType || '-'}
                        </span>
                      </td>

                      <td className="text-left px-6 py-4 whitespace-nowrap text-gray-900 font-semibold">
                        {formatCurrency(loan.loanSize)}
                        {loan.payoffValue != null &&
                          Number(loan.payoffValue) !== Number(loan.loanSize) && (
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <VisibilityIcon 
                                fontSize="small" 
                                className="text-blue-600 cursor-pointer hover:text-blue-800"
                                onClick={() => handleProceedsClick(loan)}
                              />
                              <span 
                                className="text-blue-600 cursor-pointer hover:underline"
                                onClick={() => handleProceedsClick(loan)}
                              >
                                {formatCurrency(loan.payoffValue)} payoff
                              </span>
                            </div>
                          )}
                      </td>

                      <td className="text-left px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-4 py-2 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                          {loan.status}
                        </span>
                      </td>
                      <td className="text-left px-6 py-4 whitespace-nowrap">
                        {loan.guaranteedName && loan.guaranteedName.trim() !== "" ? (
                          <span className="text-sm font-medium text-gray-900">
                            {loan.guaranteedName}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDscOpen(businessId || "", loan)}
                            sx={{
                              textTransform: 'none',
                              borderColor: 'warning.main',
                              color: 'warning.main',
                              '&:hover': {
                                borderColor: 'warning.dark',
                                backgroundColor: 'warning.50',
                              },
                            }}
                          >
                            DSC
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ArticleIcon fontSize="small" />}
                            onClick={() => onOpenQuestionnaire?.(loan)}
                            sx={{
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              borderColor: "primary.main",
                            }}
                          >
                            Transaction Summary
                          </Button>
                        </div>
                      </td>
                    </tr>

                    {expandedRows.has(loan.id) && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <Box
                            sx={{
                              p: 4,
                              bgcolor: "grey.50",
                              borderTop: "1px solid",
                              borderColor: "grey.200",
                            }}
                          >
                            {loan.assignedBanks &&
                            loan.assignedBanks.length > 0 ? (
                              loan.assignedBanks.map((banker) => (
                                <BankerDocumentsView
                                  key={banker.id}
                                  dealId={loan.id}
                                  bankerId={banker.bankId}
                                  bankerName={`${banker.bank?.firstName || ""} ${
                                    banker.bank?.lastName || ""
                                  }`}
                                  onViewTermSheet={(bankerId: string) => {
                                    setViewingLoan(loan);
                                    setViewingBankerId(bankerId);
                                  }}
                                />
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: "center", py: 4 }}
                              >
                                No bankers assigned or no documents available
                              </Typography>
                            )}
                          </Box>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {!businessId && totalPages > 1 && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-lg border">
              <Pagination
                count={totalPages}
                page={paginationData.page}
                onChange={handlePageChange}
                color="primary"
              />
              <Typography className="text-gray-600">
                Showing {paginationData.total} loan
                {paginationData.total !== 1 ? "s" : ""}
              </Typography>
            </div>
          )}
        </>
      )}

      <NewLoanDialog
        open={newLoanDialogOpen}
        customerId={customerId || ""}
        businessId={businessId || ""}
        onClose={handleCloseNewLoan}
        onSuccess={(loan) => {
          fetchLoans();
          onLoanCreated?.(loan);
        }}
      />

      <Dialog
        open={dscModalOpen}
        onClose={handleDscClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          DSC Details
          <IconButton
            aria-label="close"
            onClick={handleDscClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DSCDetails businessId={selectedBusinessId} loan={selectedLoan}/>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!viewingLoan}
        onClose={() => {
          setViewingLoan(null);
          setViewingBankerId(null);
        }}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { overflow: "hidden", m: 0, p: 0 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          Term Sheet Details
          <IconButton
            aria-label="close"
            onClick={() => {
              setViewingLoan(null);
              setViewingBankerId(null);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ m: 0, p: 2}}>
          {viewingLoan && (
            <TermSheetView
              deal={viewingLoan as Deal}
              bankerId={viewingBankerId}
            />
          )}
        </DialogContent>
      </Dialog>

      <ProceedsDialog
        open={proceedsDialogOpen}
        onClose={handleCloseProceedsDialog}
        loanName={viewingProceedsLoan?.loanName || ""}
        loanSize={viewingProceedsLoan?.loanSize || 0}
        payoffValue={viewingProceedsLoan?.payoffValue || 0}
        proceeds={viewingProceedsLoan?.proceeds || []}
        onSave={handleSaveProceeds}
        saving={savingProceeds}
      />
    </div>
  );
};

export default LoansTable;
